import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { authService } from "../../services/authService";
import { supabase } from "../../lib/supabase"; // ✅ IMPORTANTE
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Icon from "../../components/AppIcon";

const SignUp = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    setFormData((p) => ({ ...p, [e?.target?.name]: e?.target?.value }));
    setError("");
    setSuccess("");
  };

  const validateForm = () => {
    if (!formData?.fullName || !formData?.email || !formData?.password) {
      setError("Por favor, completa todos los campos requeridos");
      return false;
    }
    if (formData?.password?.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres");
      return false;
    }
    if (formData?.password !== formData?.confirmPassword) {
      setError("Las contraseñas no coinciden");
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData?.email)) {
      setError("Por favor, ingresa un correo electrónico válido");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError("");
    setSuccess("");

    if (!validateForm()) return;

    setLoading(true);

    try {
      // ✅ 1) Captura referral guardado desde landing (?ref=...)
      const referralCode = localStorage.getItem("referral_code");

      // ✅ 2) SignUp sin confirmación -> session inmediata
      const { data, error } = await authService.signUp(
        formData.email,
        formData.password,
        formData.fullName,
        formData.phone,
        "advisor"
      );

      if (error) {
        const msg = (error.message || "").toLowerCase();
        if (msg.includes("already") || msg.includes("registered")) {
          setError("Este correo ya está registrado. Por favor, inicia sesión.");
        } else {
          setError(error.message || "Error al crear la cuenta");
        }
        setLoading(false);
        return;
      }

      // ✅ 3) Si hay sesión, aplica referral ANTES del redirect
      if (data?.session) {
        // Aplica referral si existe
        if (referralCode) {
          const { error: rpcError } = await supabase.rpc("apply_referral", {
            p_referral_code: referralCode,
          });

          // Limpia el código pase lo que pase (para que no se reaplique)
          localStorage.removeItem("referral_code");

          // Si falla, no bloquees el registro; solo loguea
          if (rpcError) {
            console.warn("apply_referral RPC error:", rpcError);
          }
        }

        setSuccess("¡Cuenta creada! Redirigiendo...");
        setTimeout(() => {
          navigate("/user-dashboard", { replace: true });
        }, 250);
        return;
      }

      // fallback raro
      setSuccess("Cuenta creada. Inicia sesión.");
      setTimeout(() => {
        navigate("/signin", { replace: true });
      }, 800);
    } catch (err) {
      console.error("SignUp error:", err);
      setError(err?.message || "Error de conexión. Por favor, inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8 sm:py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-primary/10 rounded-2xl mb-3 sm:mb-4">
            <Icon name="User" size={28} className="text-primary" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
            Registro de Asesor
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Crea tu cuenta de asesor inmobiliario
          </p>
        </div>

        <div className="card p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
            {error && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-start space-x-3">
                <Icon name="AlertCircle" size={20} color="var(--color-error)" />
                <p className="text-sm text-error flex-1">{error}</p>
              </div>
            )}

            {success && (
              <div className="bg-success/10 border border-success/20 rounded-lg p-4 flex items-start space-x-3">
                <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                <p className="text-sm text-success flex-1">{success}</p>
              </div>
            )}

            <Input
              type="text"
              name="fullName"
              label="Nombre Completo"
              placeholder="Juan Pérez"
              value={formData.fullName}
              onChange={handleChange}
              required
              disabled={loading}
            />

            <Input
              type="email"
              name="email"
              label="Correo Electrónico"
              placeholder="tu@email.com"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={loading}
            />

            <Input
              type="tel"
              name="phone"
              label="Teléfono"
              placeholder="+51 987 654 321"
              value={formData.phone}
              onChange={handleChange}
              disabled={loading}
            />

            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                name="password"
                label="Contraseña"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
                disabled={loading}
              >
                <Icon name={showPassword ? "EyeOff" : "Eye"} size={20} />
              </button>
            </div>

            <div className="relative">
              <Input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                label="Confirmar Contraseña"
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
                disabled={loading}
              >
                <Icon name={showConfirmPassword ? "EyeOff" : "Eye"} size={20} />
              </button>
            </div>

            <Button type="submit" fullWidth loading={loading} disabled={loading} size="lg">
              Crear Cuenta de Asesor
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              ¿Ya tienes cuenta?{" "}
              <Link to="/signin" className="text-primary hover:underline font-medium">
                Inicia sesión
              </Link>
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              ¿Eres administrador?{" "}
              <Link to="/admin-signup" className="text-primary hover:underline font-medium">
                Regístrate como administrador
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
